import { IssuesDefaultSort } from 'pages/Desk/issues/shared';
import {
    loadLocationOverviewState,
    StoredLocationOverviewState,
    saveLocationOverviewState,
} from 'pages/Location/state/shared';
import { ReactNode, useMemo } from 'react';
import {
    createBatchFilterUpdate,
    createFilterSetters,
    DEFAULT_ISSUE_FILTERS,
} from 'shared/issue_filters_context/StoreUtils';
import {
    BaseIssuesStateMethods,
    BaseIssuesStateValues,
} from 'shared/issue_filters_context/types';
import {
    createZustandContext,
    getZustandStoreShallowHook,
    ZustandContextInitializer,
    ZustandContextProvider,
} from 'shared/zustand_context/zustand_context';

export type LocationOverviewStateValues = Readonly<
    BaseIssuesStateValues & {
        alertsAndDraftsTabIndex: number;
    }
>;

export type LocationOverviewStateMethods = Readonly<
    BaseIssuesStateMethods & {
        setAlertsAndDraftsTabIndex: (index: number) => void;
    }
>;

export type LocationOverviewState = LocationOverviewStateValues &
    LocationOverviewStateMethods;

const LocationOverviewContext = createZustandContext<LocationOverviewState>(
    'LocationOverviewStore',
);

function getLocationOverviewStateInitializer(
    locationId: string,
    userEmail: string,
    storedState: StoredLocationOverviewState | undefined,
): ZustandContextInitializer<LocationOverviewState> {
    return (set, get) => {
        const saveState = () => {
            const state = get();
            const stateToStore: StoredLocationOverviewState = {
                timeRangeFilter: state.timeRangeFilter,
                statusFilters: state.statusFilters,
                severityFilters: state.severityFilters,
                priorityFilters: state.priorityFilters,
                savingsFilters: state.savingsFilters,
                faultTypeFilters: state.faultTypeFilters,
                issueLocationFilters: state.issueLocationFilters,
                dynamicSystemFilters: state.dynamicSystemFilters,
                healthRangeFilter: state.healthRangeFilter,
                hardwareTypeFilters: state.hardwareTypeFilters,
                visibilityFilter: state.visibilityFilter,
                flaggedFilters: state.flaggedFilters,
                createdByFilter: state.createdByFilter,
                resolvedFilter: state.resolvedFilter,
                sort: state.sort,
                alertsAndDraftsTabIndex: state.alertsAndDraftsTabIndex,
            };
            saveLocationOverviewState(userEmail, locationId, stateToStore);
        };

        const setAndSave = (partial: Partial<LocationOverviewState>) => {
            set(partial);
            saveState();
        };

        const filterSetters = createFilterSetters(setAndSave);

        return {
            ...DEFAULT_ISSUE_FILTERS,
            ...storedState,
            sort: storedState?.sort ?? IssuesDefaultSort,
            alertsAndDraftsTabIndex: storedState?.alertsAndDraftsTabIndex ?? 0,

            ...filterSetters,

            setAlertsAndDraftsTabIndex: (index: number) => {
                setAndSave({ alertsAndDraftsTabIndex: index });
            },

            onFiltersChange: createBatchFilterUpdate(setAndSave),
        };
    };
}

export type LocationOverviewStoreProviderProps = Readonly<{
    locationId: string;
    userEmail: string;
    children: ReactNode;
}>;

export function LocationOverviewStoreProvider({
    locationId,
    userEmail,
    children,
}: LocationOverviewStoreProviderProps) {
    // Recreate store when location or user changes
    const initializer = useMemo(() => {
        const storedState = loadLocationOverviewState(userEmail, locationId);
        return getLocationOverviewStateInitializer(locationId, userEmail, storedState);
    }, [locationId, userEmail]);

    return (
        <ZustandContextProvider ctx={LocationOverviewContext} initializer={initializer}>
            {children}
        </ZustandContextProvider>
    );
}

export const useLocationOverviewStore = getZustandStoreShallowHook(
    LocationOverviewContext,
);
